"use client";

import styles from "@/styles/global/category-popup.module.scss";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Popup, Skeleton } from "react-vant";
import { Arrow, Cross } from "@react-vant/icons";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { closeCategoryPopup } from "@/store/slices/modalSlice";
import systemApi from "@/service/modules/systemApi";
import { usePathname, useSelectedLayoutSegments } from "next/navigation";

interface CategoryItem {
  categoryID: number;
  name: string;
  url: string;
  icon: string;
  showSubCategory: boolean;
  subCategories: SubCategoryItem[];
}

interface SubCategoryItem {
  categoryID: number;
  name: string;
  url: string;
  showSubCategory: boolean;
  subCategories: GrandChildCategoryItem[];
}

interface GrandChildCategoryItem {
  name: string;
  url: string;
}

export default function CategoryPopup() {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const layoutSegments = useSelectedLayoutSegments();

  const categoryPopupRef = useRef<HTMLDivElement>(null);
  const categoryListRef = useRef<HTMLDivElement>(null);
  const hasAutoExpandedRef = useRef<boolean>(false);
  const visible = useAppSelector((state) => state.modal.categoryPopupVisible);
  const [categoryData, setCategoryData] = useState<CategoryItem[]>([]);
  const [categoryListHeight, setCategoryListHeight] = useState<string>('');

  const isCategoryRoute = layoutSegments?.[0] === "category";

  // 从 URL 中提取 categoryPath，兼容 /category/... 与直接 /... 的重写场景
  const getCurrentCategoryPath = () => {
    if (!pathname) return '';
    // 去掉尾部多余斜杠，保持统一
    const normalizedPath = pathname.replace(/\/+$/, '') || '/';
    if (normalizedPath === '/' || normalizedPath === '/category') return '';

    if (normalizedPath.startsWith('/category/')) {
      return normalizedPath.replace(/^\/category/, '') || '';
    }

    return isCategoryRoute ? normalizedPath : '';
  };

  const currentCategoryPath = isCategoryRoute ? getCurrentCategoryPath() : '';
  // 检查分类项是否匹配当前路径
  const isCategoryMatched = (url: string) => {
    if (!currentCategoryPath || !url) return false;
    const normalizePath = (value: string) => {
      if (!value) return '';
      // 去掉结尾的所有斜杠
      let normalized = value.replace(/\/+$/, '');
      // 确保以 / 开头
      if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
      }
      return normalized;
    };

    const normalizedUrl = normalizePath(url);
    const normalizedCurrentPath = normalizePath(currentCategoryPath);

    return normalizedUrl.includes(normalizedCurrentPath);
  };

  const getCrazyDealData = async () => {
    try {
      const res = await systemApi.getCategoryTree();
      if (res.data && res.data.tree && res.data.tree.length) {
        res.data.tree.forEach((v: CategoryItem) => {
          v.showSubCategory = false;

          v.subCategories.forEach((v1: SubCategoryItem) => {
            v1.showSubCategory = false;
          });
        });
        setCategoryData(res.data.tree);
        // sessionStorageService.setItem('categoryTree', JSON.stringify(res.data.tree))
      }
    } catch {
      return { notFound: true };
    }
  };

  useEffect(() => {
    getCrazyDealData();
  }, []);

  // 计算并设置categoryList的高度
  useEffect(() => {
    if (visible) {
      const calculateHeight = () => {
        const contentElement = document.getElementById('categoryPopupContent');
        const titleElement = document.getElementById('categoryPopupTitle');
        
        if (contentElement && titleElement) {
          const contentHeight = contentElement.clientHeight;
          const titleHeight = titleElement.clientHeight;
          const listHeight = contentHeight - titleHeight;
          
          setCategoryListHeight(`${listHeight}px`);
        }
      };
      
      // 初始计算
      setTimeout(calculateHeight, 100);
      
      // 监听窗口大小变化，重新计算高度
      window.addEventListener('resize', calculateHeight);
      
      return () => {
        window.removeEventListener('resize', calculateHeight);
      };
    }
  }, [visible]);
  
  // 当分类数据变化时重新计算高度
  useEffect(() => {
    if (visible && categoryData.length > 0) {
      setTimeout(() => {
        const contentElement = document.getElementById('categoryPopupContent');
        const titleElement = document.getElementById('categoryPopupTitle');
        
        if (contentElement && titleElement) {
          const contentHeight = contentElement.clientHeight;
          const titleHeight = titleElement.clientHeight;
          const listHeight = contentHeight - titleHeight;
          
          setCategoryListHeight(`${listHeight}px`);
        }
      }, 100);
    }
  }, [categoryData, visible]);

  // 当弹窗打开时，自动展开包含匹配分类的父级分类，并滚动到顶部
  useEffect(() => {
    // 重置标志当弹窗关闭时
    if (!visible) {
      hasAutoExpandedRef.current = false;
      return;
    }

    if (visible && currentCategoryPath && categoryData.length > 0 && !hasAutoExpandedRef.current) {
      // 查找匹配的分类项
      const findMatchedCategory = () => {
        for (const category of categoryData) {
          // 检查根分类
          if (isCategoryMatched(category.url)) {
            return { rootCategory: category.categoryID, subCategory: null, grandChild: null };
          }
          
          // 检查子分类
          if (category.subCategories) {
            for (const subCategory of category.subCategories) {
              if (isCategoryMatched(subCategory.url)) {
                return { rootCategory: category.categoryID, subCategory: subCategory.categoryID, grandChild: null };
              }
              
              // 检查孙子分类
              if (subCategory.subCategories) {
                for (const grandChild of subCategory.subCategories) {
                  if (isCategoryMatched(grandChild.url)) {
                    return { rootCategory: category.categoryID, subCategory: subCategory.categoryID, grandChild: grandChild.url };
                  }
                }
              }
            }
          }
        }
        return null;
      };

      const matched = findMatchedCategory();
      
      if (matched) {
        // 标记已处理，避免重复执行
        hasAutoExpandedRef.current = true;
        
        // 自动展开匹配的分类
        const data = JSON.parse(JSON.stringify(categoryData));
        
        data.forEach((item: CategoryItem) => {
          if (item.categoryID === matched.rootCategory) {
            // 展开根分类
            item.showSubCategory = true;
            
            // 如果有子分类匹配，也展开子分类
            if (matched.subCategory && item.subCategories) {
              item.subCategories.forEach((subItem: SubCategoryItem) => {
                if (subItem.categoryID === matched.subCategory) {
                  subItem.showSubCategory = true;
                }
              });
            }
          }
        });
        
        setCategoryData(data);
        
        // 延迟滚动到匹配的分类项，确保 DOM 已渲染
        // 使用双重延迟确保 DOM 完全渲染（特别是三级分类需要展开）
        setTimeout(() => {
          // 使用 requestAnimationFrame 确保浏览器完成渲染
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (categoryListRef.current) {
                // 查找匹配的分类项元素
                let targetElement: HTMLElement | null = null;
                
                // 根据匹配的类型查找对应的 DOM 元素
                if (matched.grandChild) {
                  // 查找孙子分类 - 通过 URL 精确匹配
                  // 先找到对应的子分类容器
                  const subCategoryContainer = categoryListRef.current.querySelector(
                    `[data-subcategory-id="${matched.subCategory}"]`
                  );
                  
                  if (subCategoryContainer) {
                    // 在子分类容器内查找所有孙子分类元素
                    const grandChildElements = subCategoryContainer.querySelectorAll(
                      `.${styles["grand-child-content"]}`
                    );
                    
                    for (let i = 0; i < grandChildElements.length; i++) {
                      const el = grandChildElements[i] as HTMLElement;
                      const url = el.getAttribute('data-category-url');
                      if (url && isCategoryMatched(url)) {
                        targetElement = el;
                        break;
                      }
                    }
                  }
                  
                  // 如果还没找到，尝试全局查找所有三级分类
                  if (!targetElement) {
                    const allGrandChildElements = categoryListRef.current.querySelectorAll(
                      `.${styles["grand-child-content"]}`
                    );
                    for (let i = 0; i < allGrandChildElements.length; i++) {
                      const el = allGrandChildElements[i] as HTMLElement;
                      const url = el.getAttribute('data-category-url');
                      if (url && isCategoryMatched(url)) {
                        targetElement = el;
                        break;
                      }
                    }
                  }
                } else if (matched.subCategory) {
                  // 查找子分类 - 优先查找 sub-category-content
                  const subCategoryElement = categoryListRef.current.querySelector(
                    `[data-subcategory-id="${matched.subCategory}"]`
                  );
                  if (subCategoryElement) {
                    targetElement = subCategoryElement as HTMLElement;
                  }
                } else {
                  // 查找根分类 - 优先查找根分类的 content 或 all-category-link
              // 优先定位 category-item-sub-content 容器
              const rootSubContent = categoryListRef.current.querySelector(
                `[data-root-category-id="${matched.rootCategory}"]`
              );
              if (rootSubContent) {
                targetElement = rootSubContent as HTMLElement;
              } else {
                // 其次查找 all-category-link（如果存在）
                const allCategoryLink = categoryListRef.current.querySelector(
                  `[data-category-id="${matched.rootCategory}"].${styles["all-category-link"]}`
                );
                if (allCategoryLink) {
                  targetElement = allCategoryLink as HTMLElement;
                } else {
                  // 最后查找根分类的 content
                  const rootCategoryElement = categoryListRef.current.querySelector(
                    `[data-category-id="${matched.rootCategory}"]`
                  );
                  if (rootCategoryElement) {
                    targetElement = rootCategoryElement as HTMLElement;
                  }
                }
                  }
                }
                
                // 如果找到目标元素，滚动到该元素顶部
                if (targetElement && categoryListRef.current) {
                  const container = categoryListRef.current;
                  
                  // 使用 getBoundingClientRect 计算元素相对于滚动容器的位置
                  const containerRect = container.getBoundingClientRect();
                  const elementRect = targetElement.getBoundingClientRect();
                  
                  // 计算目标元素相对于滚动容器顶部的距离
                  const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
                  
                  // 滚动到目标元素顶部
                  container.scrollTop = relativeTop;
                } else {
                  // 如果没找到，滚动到顶部
                  categoryListRef.current.scrollTop = 0;
                }
              }
            }, 100);
          });
        }, 300);
      }
    }
  }, [visible, currentCategoryPath, categoryData.length]);

  const handleViewDetail = (url: string) => {
    dispatch(closeCategoryPopup());

    let targetUrl = url || "";

    // 确保以斜杠开头
    if (!targetUrl.startsWith("/")) {
      targetUrl = `/${targetUrl}`;
    }

    // 确保带有 /category 前缀
    if (!targetUrl.startsWith("/category")) {
      // 避免出现重复的斜杠
      targetUrl = `/category${targetUrl.startsWith("/category") ? "" : targetUrl}`;
    }

    window.location.href = targetUrl;
  };

  const handleClose = () => {
    dispatch(closeCategoryPopup());
  };

  const handleCategoryClick = (
    rootCategory: number,
    subCategory: number | string
  ) => {
    const data = JSON.parse(JSON.stringify(categoryData));
    if (!subCategory) {
      // 如果没有子分类，则直接切换根分类的展示
      data.forEach((item: CategoryItem) => {
        if (item.categoryID == rootCategory) {
          item.showSubCategory = !item.showSubCategory;
        }
      });
    }
    {
      // 如果有子分类，证明根分类无需处理，直接切换子分类的展示
      data.forEach((item: CategoryItem) => {
        if (item.categoryID == rootCategory && item.subCategories && item.subCategories.length > 0) {
          item.subCategories.forEach((v1: SubCategoryItem) => {
            if (v1.categoryID == subCategory) {
              v1.showSubCategory = !v1.showSubCategory;
            }
          });
        }
      });
    }

    setCategoryData(data);
  };

  return (
    <div className={styles["comp-category-popup"]} ref={categoryPopupRef}>
      <Popup
        teleport={() => categoryPopupRef.current || document.body}
        visible={visible}
        onClose={handleClose}
        onClickOverlay={handleClose}
        position="left"
        closeOnPopstate={true}
        closeOnClickOverlay={false}
        safeAreaInsetBottom={true}
        className={styles["category-popup-box"]}
        style={{ width: "100%", height: "100%", background: "#DDDDDD" }}
        overlayStyle={{ background: "rgba(0, 0, 0, 0.83)" }}
      >
        <div className={styles["category-popup-content"]} id="categoryPopupContent">
          {
            visible ? (
              <div className={styles["category-popup-close"]} onClick={handleClose}>
                <Cross color={'#FFFFFF'} fontSize={40} />
              </div>
            ) : null
          }
          <div className={styles["category-popup-title"]} id="categoryPopupTitle">All Categories</div>

          {
            categoryData && categoryData.length > 0 ? (
              <div 
                className={styles["category-list"]} 
                id="categoryList"
                ref={categoryListRef}
                style={{ height: categoryListHeight || 'calc(100vh - 103px)' }}
              >
                {categoryData.map((category: CategoryItem) => {
                  const isRootMatched = isCategoryMatched(category.url);
                  
                  return (
                    <div className={styles["category-item"]} key={category.categoryID}>
                      <div
                        className={styles["category-item-content"]}
                        onClick={() => handleCategoryClick(category.categoryID, "")}
                        data-category-id={category.categoryID}
                        data-category-url={category.url}
                      >
                        <Image
                          className={styles["category-icon"]}
                          src={`${process.env.IMAGE_DOMAIN}/svg/category/icon-category-${category.categoryID}.svg`}
                          alt={category.name}
                          width={20}
                          height={20}
                        ></Image>

                        <div className={styles["category-name"]}>{category.name}</div>

                        <Arrow
                          className={styles["arrow-icon"]}
                          color="#DDDDDD"
                          rotate={category.showSubCategory ? 90 : 0}
                        />
                      </div>
                      {category.showSubCategory ? (
                        <div
                          className={styles["category-item-sub-content"]}
                          data-root-category-id={category.categoryID}
                        >
                          <div 
                            className={`${styles["all-category-link"]} ${isRootMatched ? styles["current-category"] : ""}`} 
                            onClick={() => handleViewDetail(`/${category.url}`)}
                            data-category-id={category.categoryID}
                            data-category-url={category.url}
                          >
                            All {category.name}
                          </div>
                          {category.subCategories.map(
                            (subCategory: SubCategoryItem, subIndex: number) => {
                              const isSubMatched = isCategoryMatched(subCategory.url);
                              
                              return (
                          <div
                            className={styles["sub-category-item"]}
                            key={`${subCategory.name}-${subIndex}`}
                          >
                            <div
                              className={`${styles["sub-category-content"]} ${isSubMatched ? styles["current-category"] : ""}`}
                              onClick={() =>
                                handleCategoryClick(
                                  category.categoryID,
                                  subCategory.categoryID
                                )
                              }
                              data-subcategory-id={subCategory.categoryID}
                              data-category-url={subCategory.url}
                            >
                              <div className={styles["arrow-icon-box"]}>
                                <Arrow className={styles["arrow-icon"]} rotate={subCategory.showSubCategory ? 90 : 0} />
                              </div>
                              <div 
                                className={`${styles["sub-category-name"]} ${isSubMatched ? styles["current-category"] : ""}`} 
                                onClick={() => handleViewDetail(`/${subCategory.url}`)}
                              >
                                {subCategory.name}
                              </div>
                            </div>
                                  {subCategory.showSubCategory &&
                                  subCategory.subCategories &&
                                  subCategory.subCategories.length > 0
                                    ? subCategory.subCategories.map(
                                    (grandChild: GrandChildCategoryItem, grandIndex: number) => {
                                      const isGrandMatched = isCategoryMatched(grandChild.url);
                                      
                                      return (
                                        <div
                                          className={`${styles["grand-child-content"]} ${isGrandMatched ? styles["current-category"] : ""}`}
                                          key={`${grandChild.name}-${grandIndex}`}
                                          onClick={() => handleViewDetail(`/${grandChild.url}`)}
                                          data-category-url={grandChild.url}
                                        >
                                          {grandChild.name}
                                        </div>
                                      );
                                    }
                                      )
                                    : null}
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

            ) : <Skeleton rowHeight={14} style={{ paddingTop: '20px' }}></Skeleton>
          }
        </div>
      </Popup>
    </div>
  );
}
